import {PrismicRichText} from '@prismicio/react'
import type {CarouselSliceDefault} from 'prismicio-types'
import type {FC} from 'react'
import {useState} from 'react'
import 'swiper/css'
import 'swiper/css/pagination'
import type {SwiperClass} from 'swiper/react'
import {GridNavigation} from './components/GridNavigation'
import {LeftSideSeparators, RightSideSeparators} from './components/Separators'
import {Slider} from './components/Slider'
import {useProgressBarRef} from './useProgressBarRef'

/**
 * Props for `Carousel`.
 */
export type CarouselDefaultProps = {
  slice: CarouselSliceDefault & {
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
}

/**
 * Component for "Carousel" Slices.
 */
export const CarouselDefault: FC<CarouselDefaultProps> = ({slice, componentIndex, pageUid}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const {cards} = slice.primary
  const {progressBarRef, updateProgress} = useProgressBarRef()

  return (
    <section
      className="flex flex-col gap-6 bg-green-dark pt-[100px] md:items-center md:gap-12 md:pt-[140px]"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark="true"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-3 px-5 md:gap-4 md:px-[64px] lg:px-5 xl:px-0">
        <PrismicRichText
          components={{
            heading1: ({children}) => (
              <h1 className="text-32 font-normal leading-9 tracking-[.32px] text-moss-white max-md:max-w-[334px] md:text-64 md:leading-[64px] md:tracking-[.64px]">
                {children}
              </h1>
            ),
          }}
          field={slice.primary.title}
        />
        <PrismicRichText
          components={{
            paragraph: ({children}) => <p className="text-tg-body-l text-moss-white/[70%]">{children}</p>,
          }}
          field={slice.primary.description}
        />
      </div>
      <div className="relative isolate flex w-full flex-col items-center overflow-hidden">
        <div className="absolute inset-0 z-10 h-2.5 bg-gradient-to-b from-green-dark to-transparent md:h-5" />
        <div className="ml-[-1px] w-full md:grid md:grid-cols-[72px_minmax(auto,1176px)_72px] lg:grid-cols-[auto_minmax(auto,1176px)_auto]">
          <LeftSideSeparators />
          <Slider
            setActiveIndex={setActiveIndex}
            setSwiperInstance={setSwiperInstance}
            updateProgress={updateProgress}
            cards={cards}
            sliceType={slice.slice_type}
            componentIndex={componentIndex}
            pageUid={pageUid}
            duration={slice.primary.duration}
          />
          <RightSideSeparators />
        </div>
        <GridNavigation
          progressBarRef={progressBarRef}
          swiperInstance={swiperInstance}
          activeIndex={activeIndex}
          elements={cards.length}
          isDarkMode
        />
      </div>
    </section>
  )
}
